/**
 * Step 2 — Identity & RACI.
 *
 * Name, one-line summary (200 chars, counted live), department, and the five RACI
 * roles with the source's role descriptions verbatim.
 *
 * Only six fields gate progression — name, summary, departmentId, owner,
 * raciResponsible and raciInfoSec — so raciLegal and raciInformed are deliberately
 * NOT marked required (RACI_ROLES carries that flag per role).
 *
 * Department is a real foreign key into the Departments register (`departmentsApi`),
 * not free text — matching `ProcessingActivityDetail.tsx`'s `departmentId` +
 * `react-select` pattern exactly, rather than the hardcoded suggestion list this step
 * used before. A record written before this change has a plain department NAME in what
 * is now the `departmentId` slot; it won't resolve to any real option, so `optionFor`
 * returns null and the picker shows its placeholder rather than the stale name. That's a
 * one-click re-pick, not a data loss — `departmentId` is in `LIVE_EDITABLE_ATTRIBUTES` on
 * the server, so it's writable at every lifecycle stage, including on an active record.
 */

import React, { useMemo } from 'react';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import { RACI_ROLES } from '../../../constants/aiGovernance';
import { departmentsApi, peopleApi } from '../../../services/api';
import { reactSelectStyles } from '../../../utils/reactSelectStyles';
import type { Department, Person, AiUseCase } from '../../../types/models';
import type { StepProps } from './types';

interface SelectOption { value: string; label: string }

const SUMMARY_MAX = 200;
/** Warn before the hard maxLength stops typing rather than at the wall. */
const SUMMARY_WARN = 180;

const Required: React.FC = () => <span style={{ color: 'var(--bs-danger)' }}>*</span>;

const Step2Identity: React.FC<StepProps> = ({ useCase, update }) => {
  const summaryLength = (useCase.summary || '').length;

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });
  const departmentOptions: SelectOption[] = useMemo(
    () => departments.map(d => ({ value: d.departmentId, label: d.name })),
    [departments],
  );

  // RACI roles marked personPicker (owner, raciResponsible, raciInfoSec, raciLegal) are real
  // People-register foreign keys, same pattern as departmentId above - raciInformed stays free
  // text since it names a group/audience, not one person.
  //
  // The Person type's personName field is a legacy/fallback shape - the real People-register
  // records carry firstName/lastName instead (confirmed live against the DynamoDB table), so
  // this queries with a locally-widened type rather than trusting personName alone, which would
  // render every option blank.
  const { data: people = [] } = useQuery<Array<Person & { firstName?: string; lastName?: string }>>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });
  const personOptions: SelectOption[] = useMemo(
    () => people.map(p => ({
      value: p.personId,
      label: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.personName || p.personId,
    })),
    [people],
  );

  /** Resolve a stored id back to an option, tolerating a deleted person/department or a legacy
   *  free-text value that predates a field becoming a real foreign key. */
  const optionFor = (options: SelectOption[], id: string): SelectOption | null =>
    (id && options.find(o => o.value === id)) || null;

  return (
    <>
      <p className="np-sub mb-4">
        Give it a clear identity so it's easy to find later. You can edit any of this at any time.
      </p>

      <div className="np-field">
        <label htmlFor="ai-uc-name">Use Case Name <Required /></label>
        <input
          id="ai-uc-name"
          type="text"
          className="np-input"
          placeholder="e.g. Customer Support Chatbot, AI Code Review Assistant"
          value={useCase.name}
          onChange={e => update({ name: e.target.value })}
        />
        <p className="np-hint">A short name everyone on the team will recognise.</p>
      </div>

      <div className="np-field">
        <label htmlFor="ai-uc-summary">One-line Summary <Required /></label>
        <input
          id="ai-uc-summary"
          type="text"
          className="np-input"
          maxLength={SUMMARY_MAX}
          placeholder="e.g. Uses AI to answer customer FAQs and route complex queries to the right agent"
          value={useCase.summary}
          onChange={e => update({ summary: e.target.value })}
        />
        <div className="d-flex justify-content-between align-items-baseline gap-2">
          <p className="np-hint">In one sentence, what does this AI do?</p>
          <p
            className={`np-hint np-tabular ${summaryLength > SUMMARY_WARN ? 'text-warning' : ''}`}
            aria-live="polite"
          >
            {summaryLength}/{SUMMARY_MAX}
          </p>
        </div>
      </div>

      <div className="np-field" style={{ maxWidth: '32rem' }}>
        <label htmlFor="ai-uc-department">Department / Team <Required /></label>
        <Select
          inputId="ai-uc-department"
          options={departmentOptions}
          value={optionFor(departmentOptions, useCase.departmentId)}
          onChange={opt => update({ departmentId: opt?.value ?? '' })}
          placeholder="Which team is this for?"
          isClearable
          isSearchable
          menuPortalTarget={document.body}
          styles={reactSelectStyles()}
        />
        <p className="np-hint">The team that will use or own this AI capability.</p>
      </div>

      <hr className="np-divider" />

      <div className="mb-3">
        <div className="np-fieldset-legend">Roles &amp; Accountability (RACI)</div>
        <p className="np-sub mb-0">
          Who does what? This separates the person who <em>builds</em> it from the person who
          <em> owns</em> it — a critical distinction for AI governance.
        </p>
      </div>

      {RACI_ROLES.map(role => {
        const inputId = `ai-uc-${role.field}`;
        return (
          <div className="np-field" key={role.field} style={role.personPicker ? { maxWidth: '32rem' } : undefined}>
            <label htmlFor={inputId} className="d-flex align-items-center gap-2 flex-wrap">
              <span>{role.label} {role.required && <Required />}</span>
              <span className={`np-chip np-tone-${role.tone}`}>{role.raci}</span>
            </label>
            {role.personPicker ? (
              <Select
                inputId={inputId}
                options={personOptions}
                value={optionFor(personOptions, useCase[role.field] ?? '')}
                onChange={opt => update({ [role.field]: opt?.value ?? '' } as Partial<AiUseCase>)}
                placeholder={role.placeholder}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                styles={reactSelectStyles()}
              />
            ) : (
              <input
                id={inputId}
                type="text"
                className="np-input"
                placeholder={role.placeholder}
                value={useCase[role.field] ?? ''}
                // The key is a union of five known AiUseCase string fields; the cast keeps
                // the computed-property patch typed as a Partial rather than a string map.
                onChange={e => update({ [role.field]: e.target.value } as Partial<AiUseCase>)}
              />
            )}
            <p className="np-hint">{role.description}</p>
          </div>
        );
      })}
    </>
  );
};

export default Step2Identity;
